import React from 'react';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Check, X, DollarSign, Globe, ChevronRight, Gift, Phone } from 'lucide-react';
import Flag from '../components/Flag';
import CallRateCalculator from '../components/CallRateCalculator';
import SEO from '../components/SEO';

const LandingPage = () => {
  const { token } = useAuth();
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const flags = [
    {name: "united states of america", isoCode: 'us', emoji: '🇺🇸'}, 
    {name: "great britian", isoCode: 'gb', emoji: '🇬🇧'}, 
    {name: "india", isoCode: 'in', emoji: '🇮🇳'}, 
    {name: "france", isoCode: 'fr', emoji: '🇫🇷'}, 
    {name: "germany", isoCode: 'de', emoji: '🇩🇪'}, 
    {name: "brazil", isoCode: 'br', emoji: '🇧🇷'}, 
    {name: "denmark", isoCode: 'dk', emoji: '🇩🇰'}, 
    {name: "spain", isoCode: 'es', emoji: '🇪🇸'}, 
    {name: "turkey", isoCode: 'tr', emoji: '🇹🇷'}, 
    {name: "united arab emirates", isoCode: 'ae', emoji: '🇦🇪'}, 
    {name: "argentina", isoCode: 'ar', emoji: '🇦🇷'}
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getStartLink = token ? '/call' : '/register';

  return (
    <>
    <SEO
      title="DialVia | Make International Calls Online — Start for Free"
      description="Call mobile and landline numbers in 150+ countries right from your browser. No downloads. No subscription. Use your own number. Get free credit to start."
      canonical="https://dialvia.com/"
    />
    <div className="text-gray-700 font-sans -mx-4 -my-6 sm:-mx-6 md:-mx-10 lg:-mx-12 xl:-mx-16 2xl:-mx-20 bg-white">
      <style>{`
        .flag-slider {
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          height: 1.5rem;
          width: 1.5rem;
          position: relative;
        }
        .flag-inner {
          display: inline-block;
          animation: slideFlag 0.6s ease-in-out;
          line-height: 1.5rem;
        }
        @keyframes slideFlag {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .fade-in-up {
          opacity: 0;
          transform: translateY(10px);
          animation: fadeInUp 0.8s ease-out forwards;
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .gradient-text {
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          background-image: linear-gradient(90deg, #6b21a8, #9333ea);
        }

        .promo-badge {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(107, 33, 168, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(107, 33, 168, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(107, 33, 168, 0);
          }
        }
      `}</style>

      {/* HERO */}
      <section className="py-12 md:py-20 px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 bg-white">
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="md:w-1/2 space-y-6">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
              <span className="mr-2"><Globe className="w-4 h-4" /></span>
              <span>150+ Countries Available</span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold leading-tight text-gray-900">
              Make Calls <span className="gradient-text">Globally</span> For Less
            </h1>
            <p className="text-xl text-gray-600">
              Whether it's a bank, government office, or support line, DialVia connects you directly to mobile or landline numbers in 150+ countries. No downloads. No top-up required to start.
            </p>
            <div className="mb-4 inline-flex items-center px-4 py-2 rounded-lg bg-purple-100 text-purple-800 font-medium promo-badge">
              <Gift className="w-5 h-5 mr-2 text-purple-700" />
              <span>New Users: Get started for <span className="font-bold text-purple-800">FREE</span>!</span>
            </div>
            <div className="flex gap-4">
              <a href={getStartLink} className="bg-purple-700 hover:bg-purple-800 text-white font-medium px-6 py-3 rounded-md transition flex items-center gap-2">
                <span>Start Calling</span>
                <ChevronRight className="w-4 h-4" />
              </a>
              <a href="/buy-credits" className="border border-purple-300 text-purple-700 font-medium px-6 py-3 rounded-md hover:bg-purple-50 transition">
                Buy Credits
              </a>
            </div>
            <a
              href="https://open-launch.com/projects/dialvia"
              target="_blank"
              rel="noopener noreferrer"
              title="Open-Launch Top 3 Daily Winner"
              className="mt-2 sm:mt-0"
            >
              <img
                src="https://open-launch.com/images/badges/top3-light.svg"
                alt="Open-Launch Top 3 Daily Winner"
                className="h-10 w-auto"
              />
            </a>
            <div className="flex items-center gap-3 pt-4">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-white border-2 border-purple-100 flex items-center justify-center text-xs font-bold">
                    <Flag country={flags[i]} />
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-500">
                <span className="font-bold text-purple-700">500+</span> calls delivered this month
              </div>
            </div>
          </div>
          <div className="md:w-1/2 bg-white rounded-2xl shadow-lg shadow-purple-300 border border-purple-100">
          <CallRateCalculator />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 px-4 sm:px-6 md:px-10 lg:px-12 xl:px-16 2xl:px-20 bg-white">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Why Choose DialVia?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The fastest way to make international calls without the ridiculous fees
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Globe className="w-8 h-8 text-purple-600" />,
                title: 'Call Institutions Anywhere',
                description: "From IRS hotlines to your bank's fraud department, reach any landline or mobile number worldwide.",
              },
              {
                icon: <Phone className="w-8 h-8 text-purple-600" />,
                title: 'Show Your Own Number',
                description: "Make your calls show up as your own phone number, so people know it's you calling. It's free and easy to set up.",
              },
              {
                icon: <Check className="w-8 h-8 text-purple-600" />,
                title: 'No App Needed',
                description: 'Just open DialVia in your browser and start calling. No downloads or setup needed.',
              },
              {
                icon: <Globe className="w-8 h-8 text-purple-600" />,
                title: 'Call 150+ Countries',
                description: 'Worldwide access from your browser. Perfect for travelers, expats, and international callers.',
              },
              {
                icon: <DollarSign className="w-8 h-8 text-purple-600" />,
                title: '2¢/min Rates',
                description: 'No hidden fees or surprise charges. We offer rates up to 50x cheaper than traditional carriers.',
              },
              {
                icon: <Gift className="w-8 h-8 text-purple-600" />,
                title: 'Free Credit for New Users',
                description: 'First-time customers do not need to buy credits to make their first call.',
              }
            ].map((feature, i) => (
              <div key={i} className="bg-white p-8 rounded-xl shadow-sm border border-purple-100 hover:shadow-md hover:shadow-purple-100 hover:border-purple-300 transition">
                <div className="mb-4 inline-block p-3 bg-purple-100 rounded-lg">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 md:py-20 px-4 bg-purple-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to make your first call
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                number: '01',
                title: 'Sign Up',
                description: 'Create an account with just your email. No phone verification or credit card needed.',
              },
              {
                number: '02',
                title: 'Make Your First Call',
                description: 'Get instant credit to try DialVia. Call any mobile or landline number, including banks or embassies.',
              },
              {
                number: '03',
                title: 'Top Up If You Need More',
                description: 'Add credits any time. They never expire and you only pay for what you use.',
              }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-bold text-purple-200 absolute -top-4 left-0">{step.number}</div>
                <div className="pt-8">
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <a href={getStartLink} className="bg-purple-700 hover:bg-purple-800 text-white font-medium px-8 py-3 rounded-md transition inline-flex items-center gap-2">
              <span>Get Started Now</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white text-gray-900">
        <div className="max-w-screen-xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">See DialVia in Action</h2>
          <p className="text-gray-600 mb-10 max-w-2xl mx-auto text-lg">
            Watch how you can make an international call in under a minute — no downloads, no contracts, just a few clicks.
          </p>

          <div className="rounded-xl overflow-hidden shadow-lg max-w-4xl mx-auto">
            <video
              className="w-full rounded-xl shadow-md shadow-purple-100"
              src="/dialvia-demo.mp4"
              autoPlay
              loop
              muted
              playsInline
              controls
              preload="auto"
            />
          </div>
        </div>
      </section>


{/* Top-Up Section */}
<section className="py-12 md:py-20 px-4 bg-white">
  <div className="max-w-screen-xl mx-auto">
    {/* Headline */}
    <div className="text-center mb-12">
      <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Only Pay for What You Use</h2>
      <p className="text-xl text-gray-600 max-w-2xl mx-auto">
        No subscriptions. No expiration. Call anyone, anywhere.
      </p>
    </div>

    {/* Discount Banner */}
    <div className="text-center mb-10">
      <div className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-100 text-purple-800">
        <Gift className="w-5 h-5 mr-2 text-purple-700" />
        <span>
          New users get <span className="font-bold text-purple-900">FREE</span> credits!
        </span>
      </div>
    </div>

    {/* Rate Calculator */}
    <div className="max-w-3xl mx-auto mb-12">
      <CallRateCalculator />
    </div>

    {/* Preset Top-Up Options */}
    <div className="grid md:grid-cols-3 gap-6 text-center">
      {[
        { amount: 5, desc: "Perfect for a quick call" },
        { amount: 25, desc: "Most users start here" },
        { amount: 50, desc: "Best value with bonus credits" },
      ].map((opt, idx) => (
        <div
          key={idx}
          className="bg-white p-6 rounded-xl shadow-sm border border-purple-100 hover:border-purple-400 transition"
        >
          <h3 className="text-gray-900 text-2xl font-bold">${opt.amount}</h3>
          <p className="text-gray-600 mt-2">{opt.desc}</p>
          <Link
            to={`/buy-credits?amount=${opt.amount}`}
            className="mt-4 inline-block bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-md transition"
          >
            Top Up ${opt.amount}
          </Link>
        </div>
      ))}
    </div>

    {/* View full rates link */}
    <div className="mt-10 text-center">
      <Link to="/buy-credits#calculator" className="text-purple-700 hover:text-purple-900 font-medium">
        View all country rates →
      </Link>
    </div>
  </div>
</section>


      {/* Comparison Section */}
      <section className="py-12 md:py-20 px-4 bg-purple-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">How We Compare</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See why DialVia is the smarter choice for international calls
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse rounded-lg overflow-hidden bg-white">
              <thead className="bg-purple-100 text-gray-900">
                <tr>
                  <th className="py-4 px-6 font-semibold">Features</th>
                  <th className="py-4 px-6 text-center font-semibold">DialVia</th>
                  <th className="py-4 px-6 text-center font-semibold">Google Voice</th>
                  <th className="py-4 px-6 text-center font-semibold">Skype</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['New User Discount', true, false, false],
                  ['Browser-Based', true, true, true],
                  ['No Phone Verification', true, false, false],
                  ['Call Government/Bank Numbers', true, false, false],
                  ['Instant Setup', true, false, false],
                  ['No Subscription', true, true, false],
                  ['Best-in-Class Rates', true, false, false],
                  ['Free Number Spoofing', true, false, false],
                ].map(([feature, d, g, s], i) => (
                  <tr key={i} className={`border-b border-purple-100 ${i % 2 === 0 ? 'bg-white' : 'bg-purple-50'}`}>
                    <td className="py-4 px-6 font-medium text-gray-800">{feature}</td>
                    {[d, g, s].map((val, j) => (
                      <td key={j} className="py-4 px-6 text-center">
                        {val ? 
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100">
                            <Check className="w-4 h-4 text-purple-700" />
                          </div> : 
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
                            <X className="w-4 h-4 text-red-500" />
                          </div>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about our service
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "Do credits expire?",
                a: "Never. You keep your credits forever and use them when you want."
              },
              {
                q: "Can I use my own number?",
                a: "Yes. We offer free caller ID spoofing so your calls show as coming from your number."
              },
              {
                q: "Is this legal?",
                a: "Yes. We route calls via compliant carriers and simply make the process easier for you."
              },
              {
                q: "What if I don't like it?",
                a: "You get a full refund if requested within 7 days — no questions asked."
              },
              {
                q: "What are your rates?",
                a: "Check our rate calculator to see exact pricing for your destination country."
              },
              {
                q: "Do I need to download anything?",
                a: "No. DialVia works directly in your browser — no downloads or installations required."
              }
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-purple-100 hover:border-purple-300 transition">
                <h3 className="font-semibold text-lg mb-2 text-gray-900">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-10 text-center">
            <Link to="/faq" className="text-purple-700 hover:text-purple-900 font-medium">
              View all FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 md:py-20 px-4 bg-purple-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Trusted Worldwide</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
              Already routed calls across 100+ countries
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center opacity-70">
              {flags.slice(0, 5).map((flag, i) => (
                <div key={i} className="flex items-center justify-center">
                  <div className="text-4xl"><Flag country={flag} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4 bg-purple-700 text-white">
        <div className="max-w-screen-xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 text-white mb-6">
            <Gift className="w-5 h-5 mr-2 text-purple-200" />
            <span>Get <span className="font-bold text-purple-100">call credit</span> for <span className="font-bold text-purple-100">FREE</span>!</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Connect to the People and Institutions That Matter
          </h2>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto mb-10">
            Make your first international call right from your browser. No setup or payment needed. If you're not satisfied, we offer a 7-day money-back guarantee.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={getStartLink} className="bg-white text-purple-800 font-bold px-8 py-4 rounded-lg hover:bg-purple-50 transition">
              Start Calling
            </a>
            <a href="/buy-credits" className="border border-purple-200 text-white font-bold px-8 py-4 rounded-lg hover:bg-purple-600 transition">
              Buy Credits
            </a>
          </div>
        </div>
      </section>

      {/* Sticky CTA for Mobile */}
      {showStickyCTA && (
        <div className="fixed bottom-0 left-0 right-0 safe-bottom bg-white border-t border-purple-100 shadow-lg z-50 sm:hidden">
          <div className="flex flex-col items-center py-2 px-4">
            <div className="text-xs text-purple-700 mb-1 font-medium">
              <Gift className="w-3 h-3 inline mr-1" />
              Sign up and get FREE credit
            </div>
            <div className="flex justify-around items-center w-full">
              <a
                href={getStartLink}
                className="bg-purple-700 text-white font-semibold px-5 py-2 rounded-md"
              >
                Sign Up
              </a>
              <a
                href="/buy-credits"
                className="border border-purple-300 text-purple-700 px-5 py-2 rounded-md"
              >
                Buy Credits
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default LandingPage;